// Typed fetch layer. Same-origin '/api/...' — proxied to wrangler in dev,
// served by Pages Functions in prod.

import type {
  AppSettings,
  Category,
  Client,
  Entry,
  Invoice,
  InvoiceSnapshot,
  MonthRollup,
  Todo,
  Punch,
  Note,
  NoteSection,
  NoteBlock,
  NoteAsset,
  Conversation,
  ChatMessage,
} from '../shared/types'

export interface PunchState {
  running: Punch | null
  segs: Punch[]
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message)
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    let body: unknown = null
    try {
      body = await res.json()
    } catch {
      /* ignore */
    }
    const msg =
      (body && typeof body === 'object' && 'message' in body && (body as any).message) ||
      `Request failed (${res.status})`
    throw new ApiError(res.status, body, String(msg))
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  // settings
  getSettings: () => req<AppSettings>('/settings'),
  updateSettings: (patch: Partial<AppSettings>) =>
    req<AppSettings>('/settings', { method: 'PUT', body: JSON.stringify(patch) }),

  // clients
  listClients: (archived = false) => req<Client[]>(`/clients${archived ? '?archived=1' : ''}`),
  getClient: (id: number) => req<{ client: Client; categories: Category[] }>(`/clients/${id}`),
  createClient: (patch: Partial<Client>) =>
    req<Client>('/clients', { method: 'POST', body: JSON.stringify(patch) }),
  updateClient: (id: number, patch: Partial<Client>) =>
    req<Client>(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),

  // categories
  listAllCategories: () => req<Category[]>('/categories'),
  createCategory: (clientId: number, patch: Partial<Category>) =>
    req<Category>(`/clients/${clientId}/categories`, { method: 'POST', body: JSON.stringify(patch) }),
  updateCategory: (id: number, patch: Partial<Category>) =>
    req<Category>(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteCategory: (id: number) => req<{ ok: true }>(`/categories/${id}`, { method: 'DELETE' }),

  // entries
  listEntries: (clientId: number, month: string) =>
    req<Entry[]>(`/clients/${clientId}/entries?month=${month}`),
  listEntriesByDate: (date: string) => req<Entry[]>(`/entries?date=${date}`),
  listEntriesByRange: (start: string, end: string) => req<Entry[]>(`/entries?start=${start}&end=${end}`),
  createEntry: (patch: Partial<Entry> & { client_id: number; entry_date: string }) =>
    req<Entry>('/entries', { method: 'POST', body: JSON.stringify(patch) }),
  updateEntry: (id: number, patch: Partial<Entry>) =>
    req<Entry>(`/entries/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteEntry: (id: number) => req<{ ok: true }>(`/entries/${id}`, { method: 'DELETE' }),

  // client-list summary
  summary: () =>
    req<{ client_id: number; month_minutes: number; uninvoiced_cents: number }[]>('/summary'),

  // dashboard / nav
  rollup: (clientId: number, month: string) =>
    req<MonthRollup>(`/clients/${clientId}/rollup?month=${month}`),
  months: (clientId: number) => req<string[]>(`/clients/${clientId}/months`),

  // invoices
  listInvoices: (clientId: number) => req<Invoice[]>(`/clients/${clientId}/invoices`),
  getInvoice: (id: number) => req<Invoice>(`/invoices/${id}`),
  previewInvoice: (
    clientId: number,
    body: { month?: string; start?: string; end?: string; issued_date?: string; due_date?: string; attach_itemized?: boolean },
  ) =>
    req<{ snapshot: InvoiceSnapshot; warnings: { no_category: number; zero_duration: number; entry_count: number } }>(
      `/clients/${clientId}/invoices/preview`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  generateInvoice: (
    clientId: number,
    body: { month?: string; start?: string; end?: string; issued_date?: string; due_date?: string; attach_itemized?: boolean },
  ) => req<Invoice>(`/clients/${clientId}/invoices/generate`, { method: 'POST', body: JSON.stringify(body) }),
  setInvoiceStatus: (id: number, status: string) =>
    req<Invoice>(`/invoices/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  releaseInvoice: (id: number) => req<{ ok: true; number_reused: boolean }>(`/invoices/${id}`, { method: 'DELETE' }),

  // todos
  listTodos: (clientId?: number, includeDone = false) => {
    const p = new URLSearchParams()
    if (clientId != null) p.set('client', String(clientId))
    if (includeDone) p.set('done', '1')
    const qs = p.toString()
    return req<Todo[]>(`/todos${qs ? `?${qs}` : ''}`)
  },
  listArchivedTodos: (clientId?: number) => {
    const p = new URLSearchParams({ done: 'only' })
    if (clientId != null) p.set('client', String(clientId))
    return req<Todo[]>(`/todos?${p.toString()}`)
  },
  createTodo: (patch: Partial<Todo>) => req<Todo>('/todos', { method: 'POST', body: JSON.stringify(patch) }),
  updateTodo: (id: number, patch: Partial<Todo>) =>
    req<Todo>(`/todos/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteTodo: (id: number) => req<{ ok: true }>(`/todos/${id}`, { method: 'DELETE' }),

  // punch clock (cloud-synced)
  getPunches: () => req<PunchState>('/punches'),
  startPunch: (clientId: number) => req<PunchState>('/punches/start', { method: 'POST', body: JSON.stringify({ client_id: clientId }) }),
  stopPunch: () => req<PunchState>('/punches/stop', { method: 'POST' }),
  deletePunch: (id: number) => req<{ ok: true }>(`/punches/${id}`, { method: 'DELETE' }),

  // notes
  listSections: (client?: number | 'general') => {
    const qs = client == null ? '' : `?client=${client}`
    return req<NoteSection[]>(`/note-sections${qs}`)
  },
  createSection: (patch: Partial<NoteSection>) =>
    req<NoteSection>('/note-sections', { method: 'POST', body: JSON.stringify(patch) }),
  updateSection: (id: number, patch: Partial<NoteSection>) =>
    req<NoteSection>(`/note-sections/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteSection: (id: number) => req<{ ok: true }>(`/note-sections/${id}`, { method: 'DELETE' }),

  listNotes: (
    opts: { section?: number; client?: number | 'general'; q?: string; pinned?: boolean; archived?: boolean } = {},
  ) => {
    const p = new URLSearchParams()
    if (opts.section != null) p.set('section', String(opts.section))
    if (opts.client != null) p.set('client', String(opts.client))
    if (opts.q) p.set('q', opts.q)
    if (opts.pinned) p.set('pinned', '1')
    if (opts.archived) p.set('archived', '1')
    const qs = p.toString()
    return req<Note[]>(`/notes${qs ? `?${qs}` : ''}`)
  },
  getNote: (id: number) => req<{ note: Note; blocks: NoteBlock[] }>(`/notes/${id}`),
  createNote: (patch: Partial<Note> & { section_id: number }) =>
    req<{ note: Note; blocks: NoteBlock[] }>('/notes', { method: 'POST', body: JSON.stringify(patch) }),
  updateNote: (id: number, patch: Partial<Note>) =>
    req<Note>(`/notes/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteNote: (id: number) => req<{ ok: true }>(`/notes/${id}`, { method: 'DELETE' }),
  duplicateNote: (id: number, patch: { section_id?: number; title?: string } = {}) =>
    req<{ note: Note; blocks: NoteBlock[] }>(`/notes/${id}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(patch),
    }),
  /** All sections regardless of client, for move-page targets. */
  listAllSections: () => req<NoteSection[]>('/note-sections?all=1'),

  // Multipart, so it bypasses the JSON `req` helper.
  uploadAttachment: async (noteId: number, file: File, pos: { x: number; y: number }) => {
    const form = new FormData()
    form.append('file', file)
    form.append('x', String(pos.x))
    form.append('y', String(pos.y))
    const res = await fetch(`/api/notes/${noteId}/attachments`, { method: 'POST', body: form })
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new Error(
        (body && typeof body === 'object' && 'message' in body && String((body as any).message)) ||
          `Upload failed (${res.status})`,
      )
    }
    return (await res.json()) as { asset: NoteAsset; block: NoteBlock }
  },
  attachmentUrl: (assetId: number) => `/api/attachments/${assetId}`,

  createBlock: (noteId: number, patch: Partial<NoteBlock>) =>
    req<NoteBlock>(`/notes/${noteId}/blocks`, { method: 'POST', body: JSON.stringify(patch) }),
  updateBlock: (id: number, patch: Partial<NoteBlock>) =>
    req<NoteBlock>(`/blocks/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteBlock: (id: number) => req<{ ok: true }>(`/blocks/${id}`, { method: 'DELETE' }),

  // chat
  listConversations: () => req<Conversation[]>('/conversations'),
  getConversation: (id: number) =>
    req<{ conversation: Conversation; messages: ChatMessage[] }>(`/conversations/${id}`),
  updateConversation: (id: number, patch: Partial<Conversation>) =>
    req<Conversation>(`/conversations/${id}`, { method: 'PUT', body: JSON.stringify(patch) }),
  deleteConversation: (id: number) => req<{ ok: true }>(`/conversations/${id}`, { method: 'DELETE' }),

  // export / import
  exportUrl: '/api/export',
  exportCsvUrl: '/api/export.csv',
  importData: (data: unknown) => req<{ ok: true }>('/import', { method: 'POST', body: JSON.stringify(data) }),
}
